import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	HostBinding,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChange,
	ViewEncapsulation,
} from "@angular/core";
import { AccountAppCommand } from "@fabric-types/app-account";
import { Store, select } from "@ngrx/store";
import { OpenIdConnectSelector } from "@obg/common.verification";
import { GtmEventAction, GtmLoginMethod } from "@obg/gtm.types/adaptive";
import { CommonState, CustomerVerificationService, VerificationType } from "@obg/ngx.common";
import { CORE_ROUTE, Jurisdiction, JurisdictionSelector, ProductType } from "@obg/ngx.core";
import { CoreContentState, ImageContent, ImageSelector } from "@obg/ngx.core.content";
import { DocumentRef, TelemetryService } from "@obg/ngx.global.evo";
import { ignoreNil } from "@obg/rxjs";
import { kebabCase } from "lodash-es";
import { Observable, Subscription } from "rxjs";
import { take, tap, withLatestFrom } from "rxjs/operators";

import { CUSTOMER_VERIFICATION_KEYS, CustomerVerificationClickedItem } from "./customer-verification.const";
import { CustomerVerificationKeys, DialogRouteConfig } from "./customer-verification.model";

@Component({
	selector: "obg-m-customer-verification-container",
	styleUrls: ["./customer-verification.container.scss"],
	templateUrl: "./customer-verification.container.html",
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class CustomerVerificationContainer implements OnDestroy, OnInit, OnChanges {
	@HostBinding("class.obg-m-customer-verification") hostClass = true;

	@Input() variant: VerificationType | undefined;
	@Input() username: string | undefined;

	@Output() dismissDialog = new EventEmitter<DialogRouteConfig>();

	jurisdiction: Jurisdiction | undefined;
	keys: CustomerVerificationKeys | undefined;
	ksaJurisdiction = Jurisdiction.Ksa;
	mgaJurisdiction = Jurisdiction.Mga;
	withRewards = false;
	showDepositButtonOnly = this.customerVerificationService.welcomeBonusSelectionFlag();
	isRewardsEnabled = true;

	image$: Observable<ImageContent> | undefined | null = null;

	private registrationType: string | undefined;
	private data$$: Subscription | undefined;

	constructor(
		private store: Store<CommonState | CoreContentState>,
		private telemetryService: TelemetryService,
		private oidcSelector: OpenIdConnectSelector,
		private imageSelector: ImageSelector,
		private customerVerificationService: CustomerVerificationService,
		private documentRef: DocumentRef
	) {
		this.documentRef.obgEventBus?.dispatch(AccountAppCommand.GetRewardsEnabled, {
			onSuccess: (isEnabled: boolean) => (this.isRewardsEnabled = isEnabled),
		});
	}

	ngOnInit() {
		this.image$ = this.store.pipe(
			select(this.imageSelector.getByKey(ProductType.Common, "customer-verification.welcome-image")),
			ignoreNil(),
			take(1)
		);
		this.data$$ = this.store
			.select(JurisdictionSelector.get)
			.pipe(
				ignoreNil(),
				withLatestFrom(this.store.select(this.oidcSelector.getToken())),
				take(1),
				tap(([jurisdiction, token]) => {
					this.registrationType = jurisdiction;
					this.jurisdiction = jurisdiction;
					if (jurisdiction === Jurisdiction.Dga && token) {
						this.registrationType = GtmLoginMethod.MitID;
					}

					this.telemetryService.track({
						event: GtmEventAction.WelcomeMessageOpen,
						RegistrationType: this.registrationType,
					});
				})
			)
			.subscribe();
	}

	ngOnDestroy() {
		this.data$$?.unsubscribe();
	}

	ngOnChanges(changes: { variant?: SimpleChange }) {
		if (!this.variant || !changes.variant) {
			return;
		}

		this.variant = kebabCase(this.variant) as VerificationType;
		this.withRewards = this.isRewardsEnabled ? this.variant === VerificationType.withRewards : false;

		this.keys = CUSTOMER_VERIFICATION_KEYS[this.variant];
	}

	navigateToAction() {
		switch (this.variant) {
			case VerificationType.created:
			case VerificationType.withRewards:
				this.dismissDialog.emit({ routeName: CORE_ROUTE.payment.deposit });
				this.trackClickEvent(CustomerVerificationClickedItem.WelcomeBonus);
				break;
			case VerificationType.verified:
				this.dismissDialog.emit({ routeName: CORE_ROUTE.auth.login });
				this.trackClickEvent(CustomerVerificationClickedItem.Login);
				break;
		}
	}

	navigateToDeposit() {
		this.trackClickEvent(CustomerVerificationClickedItem.Deposit);
		this.dismissDialog.emit({
			routeName: CORE_ROUTE.payment.deposit,
			config: { data: { skipRewardClaim: true } },
		});
	}

	dismiss() {
		this.trackClickEvent(CustomerVerificationClickedItem.EnterSite);
		this.dismissDialog.emit();
	}

	private trackClickEvent(itemClicked?: string) {
		this.telemetryService.track({
			event: GtmEventAction.WelcomeMessageClick,
			RegistrationType: this.registrationType,
			...(itemClicked && { ItemClicked: itemClicked }),
		});
	}
}
